/*
================================================================================
|                                                                              |
|  FILE: src/utils/analytics.js                                                |
|  PURPOSE: Google Analytics utility functions for tracking user interactions |
|                                                                              |
================================================================================
*/

// Initialize Google Analytics
export const initGA = () => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-TY14GVVRW1', {
      page_title: document.title,
      page_location: window.location.href,
      custom_map: {
        'custom_parameter_1': 'company_type',
        'custom_parameter_2': 'service_area'
      }
    });
  }
};

// Track page views
export const trackPageView = (pageTitle, pagePath) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-TY14GVVRW1', {
      page_title: pageTitle,
      page_location: pagePath || window.location.href,
    });
  }
};

// Track custom events
export const trackEvent = (action, category, label, value) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// Track button clicks
export const trackButtonClick = (buttonName, pageLocation) => {
  trackEvent('click', 'button', buttonName, 1);
  console.log(`Button clicked: ${buttonName} on ${pageLocation}`);
};

// Track form submissions
export const trackFormSubmission = (formName, pageLocation) => {
  trackEvent('submit', 'form', formName, 1);
  console.log(`Form submitted: ${formName} on ${pageLocation}`);
};

// Track service page visits
export const trackServiceVisit = (serviceName) => {
  trackEvent('view', 'service', serviceName, 1);
  console.log(`Service page visited: ${serviceName}`);
};

// Track contact form interactions
export const trackContactInteraction = (action, details) => {
  trackEvent(action, 'contact', details, 1);
  console.log(`Contact interaction: ${action} - ${details}`);
};

// Track scroll depth
export const trackScrollDepth = (depth) => {
  trackEvent('scroll', 'engagement', `scroll_depth_${depth}`, depth);
};

// Track time on page
export const trackTimeOnPage = (seconds) => {
  trackEvent('timing', 'engagement', 'time_on_page', seconds);
};

// Track external link clicks
export const trackExternalLink = (url) => {
  trackEvent('click', 'external_link', url, 1);
  console.log(`External link clicked: ${url}`);
};

// Track video interactions
export const trackVideoInteraction = (action, videoTitle) => {
  trackEvent(action, 'video', videoTitle, 1);
  console.log(`Video interaction: ${action} - ${videoTitle}`);
};

// Track download events
export const trackDownload = (fileName, fileType) => {
  trackEvent('download', 'file', `${fileType}_${fileName}`, 1);
  console.log(`File downloaded: ${fileName} (${fileType})`);
};

// Track social media clicks
export const trackSocialClick = (platform, action) => {
  trackEvent('click', 'social', `${platform}_${action}`, 1);
  console.log(`Social media click: ${platform} - ${action}`);
};

// Track search queries
export const trackSearch = (searchTerm) => {
  trackEvent('search', 'site_search', searchTerm, 1);
  console.log(`Search performed: ${searchTerm}`);
};

// Track newsletter signups
export const trackNewsletterSignup = (source) => {
  trackEvent('sign_up', 'newsletter', source, 1);
  console.log(`Newsletter signup: ${source}`);
};

// Track project showcase interactions
export const trackProjectInteraction = (projectName, action) => {
  trackEvent(action, 'project', projectName, 1);
  console.log(`Project interaction: ${projectName} - ${action}`);
};

// Track mobile vs desktop usage
export const trackDeviceType = () => {
  const isMobile = window.innerWidth <= 768;
  const deviceType = isMobile ? 'mobile' : 'desktop';
  trackEvent('view', 'device', deviceType, 1);
};

// Track user engagement score
export const trackEngagement = (score) => {
  trackEvent('engagement', 'user_behavior', 'engagement_score', score);
};

export default {
  initGA,
  trackPageView,
  trackEvent,
  trackButtonClick,
  trackFormSubmission,
  trackServiceVisit,
  trackContactInteraction,
  trackScrollDepth,
  trackTimeOnPage,
  trackExternalLink,
  trackVideoInteraction,
  trackDownload,
  trackSocialClick,
  trackSearch,
  trackNewsletterSignup,
  trackProjectInteraction,
  trackDeviceType,
  trackEngagement,
}; 